import { X as CloseIcon, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { modelService } from "@/lib/services";
import type { AIModel, ModelType } from "@/types";
import { EndpointProtocolFields } from "./EndpointProtocolFields";
import { ModelBehaviorFields } from "./ModelBehaviorFields";
import { readEndpointConfig, toEndpointPayload } from "./modelEndpointConfig";
import { replacementCandidates } from "./ReassignModelModal";

// Parse a numeric input value, falling back to 0 when the field is empty or invalid
// so the controlled input never receives NaN
function parseNumericInput(value: string, parse: (v: string) => number): number {
  const parsed = parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export interface ConfigureModelModalProps {
  model: AIModel;
  models?: AIModel[];
  onClose: () => void;
  onSave: () => void;
  confirmDisable?: () => Promise<boolean>;
}

export function ConfigureModelModal({
  model,
  models = [],
  onClose,
  onSave,
  confirmDisable,
}: Readonly<ConfigureModelModalProps>) {
  const { showToast } = useToast();
  const endpointConfig = readEndpointConfig(model);
  const [formData, setFormData] = useState({
    name: model.name,
    deployment_name: model.deploymentName || model.deployment_name || "",
    hosting: endpointConfig.hosting,
    api_protocol: endpointConfig.api_protocol,
    is_enabled: model.isEnabled ?? model.is_enabled ?? false,
    context_window: model.contextWindow || 0,
    max_output_tokens: model.maxOutputTokens || model.maxTokens || 0,
    cost_per_1k_input: model.costPer1kInput ?? model.costPerInputToken ?? 0,
    cost_per_1k_output: model.costPer1kOutput ?? model.costPerOutputToken ?? 0,
    display_order: model.displayOrder ?? 0,
    capabilities: model.capabilities || [],
    model_type: model.modelType || "chat",
    is_reasoning: model.isReasoning ?? false,
    supports_vision: model.supportsVision ?? false,
    supports_function_calling: model.supportsFunctionCalling !== false,
    supports_streaming: model.supportsStreaming !== false,
    supports_code_interpreter: model.supportsCodeInterpreter ?? false,
    reasoning_config: model.reasoningConfig ?? null,
    description: model.description ?? "",
    cost_tier: model.costTier ?? "",
    endpoint_url: endpointConfig.endpoint_url,
    api_key_value: "",
    is_deprecated: model.isDeprecated ?? false,
    sunset_date: model.sunsetDate ?? "",
    replacement_model_id: model.replacementModelId ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [clearApiKey, setClearApiKey] = useState(false);
  // Track whether the admin actually edited the API-key field. We must NOT send
  // api_key_value on unrelated edits, otherwise the backend re-encrypts the key
  // on every save — which turns a misconfigured CREDENTIAL_ENCRYPTION_KEY into a
  // hard failure that blocks all model edits.
  const [apiKeyDirty, setApiKeyDirty] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.deployment_name) {
      showToast("Name and deployment name are required", "warning");
      return;
    }
    if (formData.hosting === "custom" && !formData.endpoint_url.trim()) {
      showToast("Custom endpoint requires an endpoint URL", "warning");
      return;
    }
    const disabling = (model.isEnabled ?? model.is_enabled) && !formData.is_enabled;
    try {
      if (disabling && confirmDisable && !(await confirmDisable())) return;
      setSaving(true);
      const {
        cost_tier,
        endpoint_url,
        api_key_value,
        hosting,
        api_protocol,
        sunset_date,
        replacement_model_id,
        reasoning_config,
        ...rest
      } = formData;
      const payload: Record<string, unknown> = {
        ...rest,
        ...toEndpointPayload({ hosting, api_protocol, endpoint_url }),
        ...(cost_tier ? { cost_tier } : {}),
        // null clears; un-deprecating drops the stale details
        sunset_date: (rest.is_deprecated && sunset_date) || null,
        replacement_model_id: (rest.is_deprecated && replacement_model_id) || null,
        reasoning_config: rest.is_reasoning ? reasoning_config : null,
      };
      // API key — only touch the credential path when the field was actually
      // edited (dirty-tracked) or an explicit clear was requested.
      if (clearApiKey) {
        payload.clear_api_key = true;
      } else if (apiKeyDirty && api_key_value) {
        payload.api_key_value = api_key_value;
      }
      await modelService.update(model.id, payload);
      showToast("Model updated successfully", "success");
      onSave();
    } catch (err) {
      console.error("Failed to update model:", err);
      showToast(getApiErrorMessage(err, "Failed to save changes"), "error");
    } finally {
      setSaving(false);
    }
  };

  const replacementOptions = replacementCandidates(model, models);
  const storedReplacementUnlisted =
    formData.replacement_model_id !== "" && !replacementOptions.some((m) => m.id === formData.replacement_model_id);

  return (
    <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary rounded-xl border border-border-primary w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface-primary border-b border-border-primary px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-content-primary">Configure {model.name}</h2>
          <button onClick={onClose} className="p-2 hover:bg-interactive-hover rounded-lg transition-colors">
            <CloseIcon className="w-5 h-5 text-content-tertiary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="config-model-name" className="block text-sm font-medium text-content-secondary mb-1.5">
              Model Name
            </label>
            <input
              id="config-model-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
            />
          </div>

          <div>
            <label
              htmlFor="config-model-deployment-name"
              className="block text-sm font-medium text-content-secondary mb-1.5"
            >
              Deployment Name
            </label>
            <input
              id="config-model-deployment-name"
              type="text"
              value={formData.deployment_name}
              onChange={(e) => setFormData({ ...formData, deployment_name: e.target.value })}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
            />
          </div>

          {/* Endpoint + API Protocol (unified) */}
          <EndpointProtocolFields
            idPrefix="config-model"
            hosting={formData.hosting}
            apiProtocol={formData.api_protocol}
            endpointUrl={formData.endpoint_url}
            hasApiKey={model.hasApiKey}
            apiKeyValue={formData.api_key_value}
            clearApiKey={clearApiKey}
            onHostingChange={(hosting) => setFormData({ ...formData, hosting })}
            onApiProtocolChange={(api_protocol) => setFormData({ ...formData, api_protocol })}
            onEndpointUrlChange={(endpoint_url) => setFormData({ ...formData, endpoint_url })}
            onApiKeyValueChange={(api_key_value) => {
              setFormData({ ...formData, api_key_value });
              setApiKeyDirty(true);
            }}
            onClearApiKeyChange={setClearApiKey}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="config-model-context-window"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Context Window
              </label>
              <input
                id="config-model-context-window"
                type="number"
                value={formData.context_window}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    context_window: parseNumericInput(e.target.value, (v) => Number.parseInt(v, 10)),
                  })
                }
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>

            <div>
              <label
                htmlFor="config-model-max-output-tokens"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Max Output Tokens
              </label>
              <input
                id="config-model-max-output-tokens"
                type="number"
                value={formData.max_output_tokens}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_output_tokens: parseNumericInput(e.target.value, (v) => Number.parseInt(v, 10)),
                  })
                }
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="config-model-cost-input"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Cost per 1K Input Tokens ($)
              </label>
              <input
                id="config-model-cost-input"
                type="number"
                step="0.000001"
                value={formData.cost_per_1k_input}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cost_per_1k_input: parseNumericInput(e.target.value, Number.parseFloat),
                  })
                }
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>

            <div>
              <label
                htmlFor="config-model-cost-output"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Cost per 1K Output Tokens ($)
              </label>
              <input
                id="config-model-cost-output"
                type="number"
                step="0.000001"
                value={formData.cost_per_1k_output}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cost_per_1k_output: parseNumericInput(e.target.value, Number.parseFloat),
                  })
                }
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
          </div>

          {/* Description + Cost Tier */}
          <div>
            <label
              htmlFor="config-model-description"
              className="block text-sm font-medium text-content-secondary mb-1.5"
            >
              Description
            </label>
            <input
              id="config-model-description"
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Short description shown in the model selector"
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
            />
          </div>

          <div>
            <label htmlFor="config-model-cost-tier" className="block text-sm font-medium text-content-secondary mb-1.5">
              Cost Tier
            </label>
            <select
              id="config-model-cost-tier"
              value={formData.cost_tier}
              onChange={(e) => setFormData({ ...formData, cost_tier: e.target.value })}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
            >
              <option value="">Not set</option>
              <option value="$">$ — Low cost</option>
              <option value="$$">$$ — Mid cost</option>
              <option value="$$$">$$$ — High cost</option>
            </select>
          </div>

          {/* Advanced: Model Type + Capabilities */}
          <div className="border border-border-secondary rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-surface-secondary hover:bg-interactive-active text-sm font-medium text-content-secondary transition-colors"
            >
              <span>Advanced: Model Type &amp; Capabilities</span>
              <span className="text-xs text-content-quaternary">{showAdvanced ? "Hide" : "Show"}</span>
            </button>
            {showAdvanced && (
              <div className="p-4 space-y-4 border-t border-border-secondary">
                <ModelBehaviorFields
                  idPrefix="config-model"
                  modelType={formData.model_type as ModelType}
                  capabilities={{
                    is_reasoning: formData.is_reasoning,
                    supports_vision: formData.supports_vision,
                    supports_function_calling: formData.supports_function_calling,
                    supports_streaming: formData.supports_streaming,
                    supports_code_interpreter: formData.supports_code_interpreter,
                  }}
                  onModelTypeChange={(model_type) => setFormData({ ...formData, model_type })}
                  onCapabilityChange={(key, checked) => setFormData({ ...formData, [key]: checked })}
                  reasoningConfig={formData.reasoning_config}
                  onReasoningConfigChange={(reasoning_config) => setFormData({ ...formData, reasoning_config })}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 p-4 bg-surface-secondary rounded-lg">
            <input
              type="checkbox"
              id="is_enabled"
              checked={formData.is_enabled}
              onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
              className="w-4 h-4 rounded-sm border-border-secondary text-brand-primary focus:ring-brand-primary"
            />
            <label htmlFor="is_enabled" className="text-sm text-content-primary cursor-pointer">
              Enable this model for users
            </label>
          </div>

          <div className="flex items-center gap-3 p-4 bg-surface-secondary rounded-lg">
            <input
              type="checkbox"
              id="is_deprecated"
              checked={formData.is_deprecated}
              onChange={(e) => setFormData({ ...formData, is_deprecated: e.target.checked })}
              className="w-4 h-4 rounded-sm border-border-secondary text-brand-primary focus:ring-brand-primary"
            />
            <label htmlFor="is_deprecated" className="text-sm text-content-primary cursor-pointer">
              Mark as deprecated
            </label>
          </div>

          {formData.is_deprecated && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="config-model-sunset-date"
                  className="block text-sm font-medium text-content-secondary mb-1.5"
                >
                  Sunset Date
                </label>
                <input
                  id="config-model-sunset-date"
                  type="date"
                  value={formData.sunset_date}
                  onChange={(e) => setFormData({ ...formData, sunset_date: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                />
              </div>
              <div>
                <label
                  htmlFor="config-model-replacement"
                  className="block text-sm font-medium text-content-secondary mb-1.5"
                >
                  Recommended Replacement
                </label>
                <select
                  id="config-model-replacement"
                  value={formData.replacement_model_id}
                  onChange={(e) => setFormData({ ...formData, replacement_model_id: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                >
                  <option value="">None</option>
                  {storedReplacementUnlisted && (
                    <option value={formData.replacement_model_id}>
                      {models.find((m) => m.id === formData.replacement_model_id)?.name ??
                        formData.replacement_model_id}{" "}
                      (unavailable)
                    </option>
                  )}
                  {replacementOptions.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-4 border-t border-border-primary">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
